import { z } from 'zod'

// Who may see a club event. `public` events are also served, unauthenticated,
// to the club's public website; `members` events need a login (or the
// member's calendar feed token).
export const EVENT_VISIBILITIES = ['public', 'members'] as const
export type EventVisibility = (typeof EVENT_VISIBILITIES)[number]

const isoInstant = z.string().datetime({ offset: true })

const eventFields = {
  title: z.string().trim().min(1).max(200),
  description: z.string().trim().max(4000).nullish(),
  location: z.string().trim().max(200).nullish(),
  startsAt: isoInstant,
  endsAt: isoInstant,
  visibility: z.enum(EVENT_VISIBILITIES),
  // Members can sign up to attend; see intra.event_attendees.
  attendanceEnabled: z.boolean().default(false),
  // Capacity; null or absent means no limit.
  maxAttendees: z.number().int().min(1).max(10000).nullish(),
}

const endsAfterStart = (v: { startsAt: string; endsAt: string }) => Date.parse(v.endsAt) > Date.parse(v.startsAt)
const endsAfterStartIssue = { message: 'The event must end after it starts', path: ['endsAt'] }

export const CreateEventSchema = z.object(eventFields).refine(endsAfterStart, endsAfterStartIssue)
export type CreateEventInput = z.infer<typeof CreateEventSchema>

// PUT semantics: the whole event is sent, so the refinement always has both ends.
export const UpdateEventSchema = CreateEventSchema

// An event as the API returns it to a signed-in member.
export interface ClubEvent {
  eventId: number
  title: string
  description: string | null
  location: string | null
  startsAt: string
  endsAt: string
  visibility: EventVisibility
  attendanceEnabled: boolean
  maxAttendees: number | null
  // Only meaningful when attendanceEnabled: how many members are coming, and
  // whether the signed-in member is one of them.
  attendeeCount: number
  attending: boolean
  revision: number
  updatedAt: string
}

// One member on an event's attendee list. Served to signed-in members and
// admins only, never by the public events endpoint.
export interface EventAttendee {
  userId: string
  displayName: string
  joinedAt: string
}
